/** Breathing room between the anchor and the edge of the cut-out. */
export const SPOTLIGHT_PADDING = 6;
/** Corner radius of the cut-out; `clip-path: path()` needs an absolute pixel value. */
export const SPOTLIGHT_RADIUS = 8;
/** Larger than any viewport; the browser clips the outer rectangle to the element anyway. */
const OUTER_EXTENT = 100000;

export interface SpotlightHole {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const calculateSpotlightHole = (anchorRect: DOMRect, padding = SPOTLIGHT_PADDING): SpotlightHole => ({
  left: anchorRect.left - padding,
  top: anchorRect.top - padding,
  width: anchorRect.width + padding * 2,
  height: anchorRect.height + padding * 2,
});

const round = (value: number): number => Math.round(value * 100) / 100;

/**
 * Full-viewport path with a rounded hole punched out (even-odd rule). Applied as `clip-path`, it also removes the
 * hole from hit-testing, so the anchor stays clickable through the backdrop.
 */
export const spotlightClipPath = ({ left, top, width, height }: SpotlightHole, radius = SPOTLIGHT_RADIUS): string => {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  const right = left + width;
  const bottom = top + height;
  const arc = `A ${r} ${r} 0 0 1`;

  const outer = `M 0 0 H ${OUTER_EXTENT} V ${OUTER_EXTENT} H 0 Z`;
  const hole =
    `M ${round(left + r)} ${round(top)} H ${round(right - r)} ${arc} ${round(right)} ${round(top + r)} ` +
    `V ${round(bottom - r)} ${arc} ${round(right - r)} ${round(bottom)} H ${round(left + r)} ` +
    `${arc} ${round(left)} ${round(bottom - r)} V ${round(top + r)} ${arc} ${round(left + r)} ${round(top)} Z`;

  return `path(evenodd, "${outer} ${hole}")`;
};
